'use client';

import * as React from 'react';
import type { TocItem } from 'app/blog/[domain]/(standalone)/_server/types';
import clsx from 'clsx';
import { ErrorBoundary } from 'components/error-boundary';
import { IconMenu02 } from 'icon/General/menu-02';
import { useTranslations } from 'next-intl';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
  FocusableLink,
  ScrollArea,
  Separator,
} from 'ui';
import { getItemOffset, getLineOffset, TOCItem } from './toc-item';
import { TocThumb } from './toc-thumb';
import { useAnchorObserver } from './use-anchor-observer';

export type TocProps = {
  toc: TocItem[];
  children?: React.ReactNode;
};

export function TOC(props: TocProps) {
  return (
    <>
      <TOCMobile {...props} className="w-full md:hidden" />
      <TOCDesktop {...props} className="hidden md:block" />
    </>
  );
}

type TOCDesktopProps = TocProps & {
  className?: string;
};

function TOCDesktop({ className, children, ...props }: TOCDesktopProps) {
  const t = useTranslations('Blog.Post.Aside');
  return (
    <div className={clsx('flex h-full flex-col', className)}>
      <Separator />
      <div className="flex h-full flex-col gap-4 py-8">
        <h3 className="flex items-center gap-2 whitespace-nowrap text-md font-semibold text-brand-tertiary">
          <IconMenu02 className="size-5" />
          {t('Toc')}
        </h3>
        <ErrorBoundary fallback={null}>
          <TOCDesktopList {...props} />
        </ErrorBoundary>
        {children}
      </div>
    </div>
  );
}
function TOCDesktopList({ toc }: TocProps) {
  const viewRef = React.useRef<HTMLDivElement>(null);
  const containerRef = React.useRef<HTMLDivElement>(null);
  const [svg, setSvg] = React.useState<{
    path: string;
    width: number;
    height: number;
  }>();
  React.useEffect(() => {
    if (!containerRef.current) {
      return;
    }
    const container = containerRef.current;

    function onResize(): void {
      if (container.clientHeight === 0) {
        return;
      }
      let w = 0;
      let h = 0;
      const d: string[] = [];
      for (let i = 0; i < toc.length; i++) {
        const item = toc[i];
        const element: HTMLElement | null = container.querySelector(
          `a[href="#${item?.id}"]`,
        );
        if (!element) {
          continue;
        }

        const styles = getComputedStyle(element);
        const offset = getLineOffset(item?.level ?? 1) + 1;
        const top = element.offsetTop + parseFloat(styles.paddingTop);
        const bottom =
          element.offsetTop +
          element.clientHeight -
          parseFloat(styles.paddingBottom);

        w = Math.max(offset, w);
        h = Math.max(h, bottom);

        d.push(`${i === 0 ? 'M' : 'L'}${offset} ${top}`);
        d.push(`L${offset} ${bottom}`);
      }

      setSvg({
        path: d.join(' '),
        width: w + 1,
        height: h,
      });
    }

    const observer = new ResizeObserver(onResize);
    onResize();

    observer.observe(container);
    return () => {
      observer.disconnect();
    };
  }, [toc]);
  useAnchorObserver(toc.map((item) => item.id));
  return (
    <ScrollArea
      className="flex h-max flex-col"
      viewportRef={viewRef as React.RefObject<HTMLDivElement>}
      viewportClassName="relative min-h-0"
    >
      {svg ? (
        <div
          className="absolute start-0 top-0 bg-quaternary rtl:-scale-x-100"
          style={{
            width: svg.width,
            height: svg.height,
            maskImage: `url("data:image/svg+xml,${
              // Inline SVG
              encodeURIComponent(
                `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${svg.width} ${svg.height}"><path d="${svg.path}" stroke="gray" stroke-width="1" fill="none" /></svg>`,
              )
            }")`,
          }}
        >
          <TocThumb containerRef={containerRef} />
        </div>
      ) : null}
      <div
        className="flex flex-col gap-4 text-md font-semibold"
        ref={containerRef}
      >
        {toc.map((item, i) => (
          <TOCItem
            key={item.id}
            item={item}
            upper={toc[i - 1]?.level}
            lower={toc[i + 1]?.level}
            containerRef={viewRef}
          />
        ))}
      </div>
    </ScrollArea>
  );
}

function TOCMobile({ children, className, toc }: TOCDesktopProps) {
  const t = useTranslations('Blog.Post.Aside');
  return (
    <div className={className}>
      <Accordion type="single" collapsible>
        <AccordionItem value="toc" className="border-b-0!">
          <AccordionTrigger className="whitespace-nowrap text-md font-semibold text-brand-tertiary">
            {t('Toc')}
          </AccordionTrigger>
          <AccordionContent>
            <div className="flex flex-col gap-4 text-md font-semibold text-tertiary">
              {toc.map((item) => (
                <FocusableLink
                  key={item.id}
                  href={`#${item.id}`}
                  style={{
                    paddingInlineStart: getItemOffset(item.level),
                  }}
                >
                  {item.text}
                </FocusableLink>
              ))}
            </div>
          </AccordionContent>
        </AccordionItem>
      </Accordion>
      {children}
    </div>
  );
}
